import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useMachine } from '@xstate/react';
import {
  Alert,
  Box,
  Card,
  Column,
  Heading,
  Row,
} from '@chargify/custom-ui-components';
import {
  NO_STATE_OPTION,
  addressMachine,
  addressSelectors,
} from '@chargify/address';
import { DataAccessContext } from '@chargify/data-access';
import { ConfigContext, GlobalStateContext } from '../../contexts';
import { customerDataSelectors } from '../../machines/customerData/selectors';

import {
  ContactDetails,
  ContactDetailsForm,
  ShippingAddress,
  ShippingAddressForm,
} from '../../components';
import DetailsSectionLoadingSkeleton from '../../components/DetailsSectionLoadingSkeleton';
import CustomFieldsContainer from '../CustomFieldsContainer';
import { SectionWrapper } from './CustomerDetailsContainer.styles';
import { useDataWithNames } from '../../hooks';

const CustomerDetailsContainer = ({}) => {
  const { customerDataService, customFieldsService } =
    useContext(GlobalStateContext);
  const { t } = useTranslation(['customer-details']);
  const {
    customerProfile: { editAllowed },
  } = useContext(ConfigContext);
  const { dataAccess } = useContext(DataAccessContext);
  const [, sendToMachine, service] = useMachine(addressMachine, {
    services: {
      getCountries: () => dataAccess.getCountries(),
      getSubdivisions: (_ctx, event) => {
        if (event.type === 'FETCH_SUBDIVISION')
          return dataAccess.getCountrySubdivisions(event.country);
      },
    },
  });

  const countries = useSelector(service, addressSelectors.getCountries);
  const subdivisions = useSelector(service, addressSelectors.getSubdivisions);

  const { isLoading: contactDetailsLoading } = useSelector(
    customerDataService,
    customerDataSelectors.getContactDataLoading
  );

  const contactDetails = useSelector(
    customerDataService,
    customerDataSelectors.getContactDetailsStatus
  );

  const shippingAddress = useSelector(
    customerDataService,
    customerDataSelectors.getShippingAddressStatus
  );

  const { data } = useSelector(
    customerDataService,
    customerDataSelectors.getCustomerDetailsData
  );

  useEffect(() => {
    customFieldsService.send('INITIALIZE');
    customerDataService.send('INITIALIZE');
  }, [customerDataService, customFieldsService]);

  const dataWithNames = useDataWithNames({ countries, subdivisions, data });

  useEffect(() => {
    sendToMachine({ type: 'FETCH_COUNTRY' });
    if (data?.country) {
      sendToMachine({ type: 'FETCH_SUBDIVISION', country: data.country });
    }
  }, [data, sendToMachine]);

  return (
    <Card>
      <Box padding={7}>
        {contactDetails.isError && (
          <Box marginBottom={5}>
            <Alert color="danger">
              <Alert.TextMessage>
                {t('error.customer_data_save_error')}
              </Alert.TextMessage>
            </Alert>
          </Box>
        )}
        <Row>
          <Column span={[24, 7]}>
            <Heading variant="h4">
              {t('contact_details.customer_details')}
            </Heading>
          </Column>
          <Column span={[24, 17]}>
            {contactDetailsLoading && <DetailsSectionLoadingSkeleton />}
            {contactDetails.isEdit || contactDetails.isSaving ? (
              <ContactDetailsForm
                onCancel={() => customerDataService.send('CANCEL')}
                values={data}
                onSubmit={(data) => {
                  customerDataService.send('SAVE', { addressData: data });
                }}
                isSaving={contactDetails.isSaving}
              />
            ) : (
              data && (
                <ContactDetails
                  editAllowed={editAllowed}
                  contactDetails={data}
                  onEdit={() =>
                    customerDataService.send('EDIT_CONTACT_DETAILS')
                  }
                />
              )
            )}
          </Column>
        </Row>
        <SectionWrapper paddingTop={7} marginTop={7}>
          {shippingAddress.isError && (
            <Box marginBottom={5}>
              <Alert color="danger">
                <Alert.TextMessage>
                  {t('error.customer_data_save_error')}
                </Alert.TextMessage>
              </Alert>
            </Box>
          )}
          <Row>
            <Column span={[24, 7]}>
              <Heading variant="h4">
                {t('shipping_address.shipping_address')}
              </Heading>
            </Column>
            <Column span={[24, 17]}>
              {contactDetailsLoading && <DetailsSectionLoadingSkeleton />}
              {shippingAddress.isEdit || shippingAddress.isSaving ? (
                <ShippingAddressForm
                  shippingAddress={data}
                  onCancel={() => customerDataService.send('CANCEL')}
                  onSubmit={(data) => {
                    if (data.state === NO_STATE_OPTION.code) {
                      data.state = '';
                    }
                    customerDataService.send('SAVE', { addressData: data });
                  }}
                  isSaving={shippingAddress.isSaving}
                />
              ) : (
                dataWithNames && (
                  <ShippingAddress
                    editAllowed={editAllowed}
                    shippingAddress={dataWithNames}
                    onEdit={() =>
                      customerDataService.send('EDIT_SHIPPING_ADDRESS')
                    }
                  />
                )
              )}
            </Column>
          </Row>
          <CustomFieldsContainer />
        </SectionWrapper>
      </Box>
    </Card>
  );
};

export default CustomerDetailsContainer;
